// src/components/CharacterDisplay.js
import React, { useState } from 'react';
import styles from './CharacterDisplay.module.css';
import { ItemData } from '../data/ItemData';
import { getItemImageSrc } from '../utils/getItemImageSrc';
import ItemTooltip from './ItemTooltip';
import NavigationTooltip from './NavigationTooltip';
import { getStatBreakdown, BASE_STATS } from '../utils/statProgressionUtils';

// This order naturally fills a 3-column grid as requested:
// Row 1: Back, Helmet, Necklace
// Row 2: MainHand, Chest, OffHand
// Row 3: Gloves, Pants, Boots
// Row 4: Ring1, Belt, Ring2
const equipmentSlotNames = [ 
  "Back", "Helmet", "Necklace",
  "MainHand", "Chest", "OffHand",
  "Gloves", "Pants", "Boots",
  "Ring1", "Belt", "Ring2"
];

const CharacterDisplay = ({ playerData, onUnequipItem, tooltipsEnabled = true }) => {
  // Get stat breakdown to show progression with error handling
  let statBreakdown;
  try {
    statBreakdown = getStatBreakdown(playerData, ItemData);
  } catch (error) {
    console.error('Error getting stat breakdown:', error);
    // Fallback to empty values if there's an error
    statBreakdown = {
      base: BASE_STATS,
      skills: { strength: 0, dexterity: 0, intelligence: 0, constitution: 0, luck: 0, defense: 0 },
      achievements: { strength: 0, dexterity: 0, intelligence: 0, constitution: 0, luck: 0, defense: 0 },
      equipment: { strength: 0, dexterity: 0, intelligence: 0, constitution: 0, luck: 0, defense: 0 }
    };
  }
  
  // Helper function to format stat with bonuses
  const formatStatWithBonus = (stat) => {
    const baseValue = BASE_STATS[stat] || 0;
    const currentValue = playerData.stats[stat] || baseValue;
    const bonus = currentValue - baseValue;
    
    if (bonus > 0) {
      return `${currentValue} (${baseValue}+${bonus})`;
    } else if (bonus < 0) {
      return `${currentValue} (${baseValue}${bonus})`;
    } else {
      return `${currentValue}`;
    }
  };
  
  // Stats order as requested:
  const statsColumn1 = [
    { key: 'Health', value: `${playerData.health.current}/${playerData.health.max}` },
    { key: 'Energy', value: `${playerData.energy.current}/${playerData.energy.max}` },
    { key: 'Mana', value: `${playerData.mana.current}/${playerData.mana.max}` },
    { key: 'Min Dmg', value: playerData.stats.damageMin },
    { key: 'Max Dmg', value: playerData.stats.damageMax },
    { key: 'Carry', value: `${playerData.currentCarryWeight.toFixed(1)} / ${playerData.carryCapacity.toFixed(0)}` },
  ];

  const statsColumn2 = [
    { key: 'Strength', value: formatStatWithBonus('strength') },
    { key: 'Dexterity', value: formatStatWithBonus('dexterity') },
    { key: 'Intelligence', value: formatStatWithBonus('intelligence') },
    { key: 'Constitution', value: formatStatWithBonus('constitution') },
    { key: 'Luck', value: formatStatWithBonus('luck') },
    { key: 'Defense', value: formatStatWithBonus('defense') },
  ];

  const getSlotKey = (slotName) => {
      return slotName.charAt(0).toLowerCase() + slotName.slice(1);
  }

  // Helper function to get tooltip text for stats
  const getStatTooltip = (statKey, value) => {
    const tooltips = {
      'Health': `Health: ${value} - Your life force. When health reaches 0, you pass out and wake up at home.`,
      'Energy': `Energy: ${value} - Your stamina for activities. Low energy reduces efficiency. At 0, you collapse from exhaustion.`,
      'Mana': `Mana: ${value} - Your magical energy used for spells, enchantments, and magical abilities.`,
      'Min Dmg': `Minimum Damage: ${value} - The lowest damage you can deal in combat. Based on equipped weapons and stats.`,
      'Max Dmg': `Maximum Damage: ${value} - The highest damage you can deal in combat. Based on equipped weapons and stats.`,
      'Carry': `Carry Weight: ${value} - How much weight you can carry. Overweight reduces movement and efficiency.`,
      'Strength': `Strength: ${value} - Increases damage, carry capacity, and unlocks certain activities. Raised by combat and physical work.`,
      'Dexterity': `Dexterity: ${value} - Improves accuracy, critical hit chance, and fishing success. Raised by precise activities.`,
      'Intelligence': `Intelligence: ${value} - Increases mana, magic damage, and crafting success. Raised by studying and magic use.`,
      'Constitution': `Constitution: ${value} - Increases health and energy. Raised by endurance activities and surviving challenges.`,
      'Luck': `Luck: ${value} - Improves rare item drops, critical hits, and success rates. Raised by achievements and rare events.`,
      'Defense': `Defense: ${value} - Reduces damage taken in combat. Improved by armor and constitution training.`
    };
    return tooltips[statKey] || `${statKey}: ${value}`;
  };

  // Helper function to safely sum stat bonuses
  const sumStatBonuses = (statObject) => {
    try {
      return Object.values(statObject || {}).reduce((a, b) => a + b, 0);
    } catch (error) {
      console.error('Error summing stat bonuses:', error);
      return 0;
    }
  };

  return (
    <div className={styles.characterDisplayContainer}>
      <h3 className={styles.title}>{playerData.name || "Character"}</h3>
      
      <div className={styles.statsSection}>
        <h4>Main Stats</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statColumn}>
            {statsColumn1.map(({ key, value }) => (
              <NavigationTooltip key={key} text={getStatTooltip(key, value)} disabled={!tooltipsEnabled}>
                <div className={styles.statItem}>
                  <span>{key}:</span>
                  <span>{value}</span>
                </div>
              </NavigationTooltip>
            ))}
          </div>
          <div className={styles.statColumn}>
            {statsColumn2.map(({ key, value }) => (
              <NavigationTooltip key={key} text={getStatTooltip(key, value)} disabled={!tooltipsEnabled}>
                <div className={styles.statItem}>
                  <span>{key}:</span>
                  <span>{value}</span>
                </div>
              </NavigationTooltip>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.equipmentSection}>
        <h4>Equipment</h4>
        <div className={styles.equipmentGrid}>
          {equipmentSlotNames.map(slotName => {
            const slotKey = getSlotKey(slotName); 
            const equippedItemId = playerData.equipment[slotKey];
            const itemInstance = equippedItemId ? playerData.inventory[equippedItemId] : null;
            const itemBaseKey = itemInstance ? itemInstance.key : null;
            const itemBaseData = itemBaseKey ? ItemData[itemBaseKey] : null;

            let isDisabled = false;
            let disabledReason = "";
            if (slotKey === 'offHand') {
                const mainHandItemInstance = playerData.equipment.mainHand ? playerData.inventory[playerData.equipment.mainHand] : null;
                const mainHandBase = mainHandItemInstance ? ItemData[mainHandItemInstance.key] : null;
                if (mainHandBase && mainHandBase.handedness === 'twoHanded') {
                    isDisabled = true;
                    disabledReason = "N/A (2H)";
                }
            }

            return (
              <ItemTooltip
                key={slotKey}
                itemKey={itemBaseKey}
                itemInstance={itemInstance}
                showStats={true}
                disabled={!itemInstance || isDisabled}
              >
                <div
                  className={`${styles.equipmentSlot} ${itemInstance ? styles.equipped : styles.empty} ${isDisabled ? styles.disabled : ''}`}
                  onClick={() => {
                      if (itemInstance && !isDisabled && onUnequipItem) { 
                          onUnequipItem(slotKey);
                      }
                  }}
                  title={isDisabled ? disabledReason : (!itemInstance ? `Empty ${slotName} slot` : itemInstance && itemBaseData ? `Equipped: ${itemBaseData.name}\nClick to unequip.` : undefined)}
                >
                  {isDisabled ? (
                      <span className={styles.slotLabel}>{disabledReason}</span>
                  ) : itemInstance && itemBaseData ? (
                    <>
                      <img
                        src={getItemImageSrc(itemBaseKey)}
                        alt={itemBaseData.name}
                        className={styles.equippedItemIcon}
                      />
                      {itemInstance.durability !== null && itemInstance.durability !== undefined && (
                          <span className={`${styles.equippedItemDurability} ${itemInstance.durability <= 0 ? styles.broken : ''}`}>
                              {itemInstance.durability}/{itemInstance.maxDurability}
                          </span>
                      )}
                    </>
                  ) : (
                    <span className={styles.slotLabel}>{slotName}</span>
                  )}
                </div>
              </ItemTooltip>
            );
          })}
        </div>
      </div>

      {/* Stat Progression Section - Now below equipment and always visible */}
      <div className={styles.progressionSection} style={{
        minHeight: '250px',
        height: 'auto'
      }}>
        <div className={styles.progressionHeader}>
          <h4>Stat Progression</h4>
        </div>
        <div className={styles.progressionContent}>
          <div className={styles.progressionGrid}>
            <div className={styles.progressionItem}>
              <span>Skill Bonuses:</span>
              <span>{sumStatBonuses(statBreakdown.skills)} total</span>
            </div>
            <div className={styles.progressionItem}>
              <span>Achievement Bonuses:</span>
              <span>{sumStatBonuses(statBreakdown.achievements)} total</span>
            </div>
            <div className={styles.progressionItem}>
              <span>Equipment Bonuses:</span>
              <span>{sumStatBonuses(statBreakdown.equipment)} total</span>
            </div>
          </div>
          <div className={styles.progressionNote}>
            <p>💡 Stats increase through skill progression (every 10 levels), achievements, and equipment!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterDisplay;